import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserPlus, FaUser, FaPhone, FaEnvelope, FaUsers, FaSpinner, FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const CreateStudent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    grade: '',
    guardian_name: '',
    guardian_email: '',
    guardian_phone: ''
  });

  // Função para verificar se o responsável é obrigatório baseado na série
  const isGuardianRequired = (grade) => {
    const optionalGrades = ['1º Médio', '2º Médio', '3º Médio', 'Graduação', 'Outros'];
    return !optionalGrades.includes(grade);
  };

  // Função para verificar se pelo menos um campo de responsável foi preenchido
  const hasGuardianData = () => {
    return formData.guardian_name || formData.guardian_email || formData.guardian_phone;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validação customizada para responsável
    const guardianRequired = isGuardianRequired(formData.grade);
    const hasPartialGuardianData = hasGuardianData();
    
    if (guardianRequired && !hasPartialGuardianData) {
      setError('Para esta série, os dados do responsável são obrigatórios.');
      setLoading(false);
      return;
    }

    // Se tem dados parciais do responsável, todos os campos são obrigatórios
    if (hasPartialGuardianData) {
      if (!formData.guardian_name || !formData.guardian_email || !formData.guardian_phone) {
        setError('Se informar dados do responsável, todos os campos (nome, email e telefone) são obrigatórios.');
        setLoading(false);
        return;
      }
    }

    try {
      const response = await axios.post(`${config.apiURL}/teacher/create-student`, formData);
      
      if (response.data) {
        let successMessage = `Aluno criado com sucesso!\nChave do aluno: ${response.data.student.access_key}`;
        
        if (response.data.student.guardian) {
          successMessage += `\nChave do responsável: ${response.data.student.guardian.access_key}`;
        } else {
          successMessage += `\nNenhum responsável foi cadastrado.`;
        }
        
        setSuccess(successMessage);
        
        // Limpar formulário
        setFormData({
          name: '',
          birth_date: '',
          grade: '',
          guardian_name: '',
          guardian_email: '',
          guardian_phone: ''
        });
        
        // Redirecionar após 3 segundos
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao cadastrar aluno');
    } finally {
      setLoading(false);
    }
  };

  const gradeOptions = [
    '1º Ano', '2º Ano', '3º Ano', '4º Ano', '5º Ano',
    '6º Ano', '7º Ano', '8º Ano', '9º Ano',
    '1º Médio', '2º Médio', '3º Médio',
    'Graduação', 'Outros'
  ];

  const selectedGradeRequiresGuardian = isGuardianRequired(formData.grade);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaUserPlus className="text-blue-600" />
            <span>Cadastrar Novo Aluno</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Adicione um novo aluno ao sistema
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md whitespace-pre-line">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Dados do Aluno */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FaUser className="text-blue-600" />
              <span>Dados do Aluno</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Digite o nome completo do aluno"
                />
              </div>

              <div>
                <label htmlFor="birth_date" className="block text-sm font-medium text-gray-700 mb-2">
                  <FaCalendarAlt className="inline mr-1" />
                  Data de Nascimento *
                </label>
                <input
                  type="date"
                  id="birth_date"
                  name="birth_date"
                  required
                  value={formData.birth_date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Campo obrigatório - usado para categorização por idade
                </p>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                  Série/Ano *
                </label>
                <select
                  id="grade"
                  name="grade"
                  required
                  value={formData.grade}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione a série/ano</option>
                  {gradeOptions.map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Dados do Responsável */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <FaUsers className="text-green-600" />
              <span>Dados do Responsável</span>
              {!selectedGradeRequiresGuardian && formData.grade && (
                <span className="text-sm font-normal text-blue-600 bg-blue-50 px-2 py-1 rounded-md">
                  Opcional
                </span>
              )}
            </h2>

            {/* Aviso sobre responsável */}
            {formData.grade && (
              <div className={`mb-4 p-3 rounded-md flex items-start space-x-2 ${
                selectedGradeRequiresGuardian 
                  ? 'bg-orange-50 border border-orange-200' 
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <FaInfoCircle className={`mt-0.5 ${
                  selectedGradeRequiresGuardian ? 'text-orange-600' : 'text-blue-600'
                }`} />
                <div className={`text-sm ${
                  selectedGradeRequiresGuardian ? 'text-orange-800' : 'text-blue-800'
                }`}>
                  {selectedGradeRequiresGuardian ? (
                    <p><strong>Responsável obrigatório</strong> para esta série. Todos os campos abaixo devem ser preenchidos.</p>
                  ) : (
                    <p><strong>Responsável opcional</strong> para esta série. Você pode deixar em branco ou preencher todos os campos se desejar cadastrar um responsável.</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="guardian_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo {selectedGradeRequiresGuardian ? '*' : ''}
                </label>
                <input
                  type="text"
                  id="guardian_name"
                  name="guardian_name"
                  required={selectedGradeRequiresGuardian}
                  value={formData.guardian_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Digite o nome completo do responsável"
                />
              </div>

              <div>
                <label htmlFor="guardian_email" className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-1" />
                  Email {selectedGradeRequiresGuardian ? '*' : ''}
                </label>
                <input
                  type="email"
                  id="guardian_email"
                  name="guardian_email"
                  required={selectedGradeRequiresGuardian}
                  value={formData.guardian_email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <label htmlFor="guardian_phone" className="block text-sm font-medium text-gray-700 mb-2">
                  <FaPhone className="inline mr-1" />
                  Telefone {selectedGradeRequiresGuardian ? '*' : ''}
                </label>
                <input
                  type="tel"
                  id="guardian_phone"
                  name="guardian_phone"
                  required={selectedGradeRequiresGuardian}
                  value={formData.guardian_phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Cadastrando...</span>
                </>
              ) : (
                <>
                  <FaUserPlus />
                  <span>Cadastrar Aluno</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Informações sobre as chaves */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            ℹ️ Sobre as Chaves de Acesso
          </h3>
          <div className="text-blue-800 space-y-2">
            <p>• Após o cadastro, será gerada uma chave única para o aluno</p>
            <p>• Se um responsável for cadastrado, ele também receberá uma chave única</p>
            <p>• As chaves têm o formato: XXXXX-ABC (5 caracteres + 3 caracteres)</p>
            <p>• O aluno e responsável (quando aplicável) compartilham os mesmos 3 últimos caracteres</p>
            <p>• <strong>Responsável é obrigatório</strong> para séries do 1º ao 9º ano</p>
            <p>• <strong>Responsável é opcional</strong> para Ensino Médio, Graduação e Outros</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStudent; 